import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, MapPin, Calendar, Euro, Users, FileText, Activity, AlertTriangle, CheckCircle, RefreshCw, LayoutGrid, CalendarDays } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ChantierForm from '@/components/forms/ChantierForm';
import { ChantierAIActions } from '@/components/AIActionButtons';
import { format, parseISO, differenceInDays, startOfMonth, endOfMonth, addMonths, isWithinInterval } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function Chantiers() {
  const [chantiers, setChantiers] = useState([]);
  const [filteredChantiers, setFilteredChantiers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'planning'
  const [statusTab, setStatusTab] = useState('all'); // 'all' | 'upcoming' | 'in_progress' | 'done'
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Chantiers - Reno Resolve';
    fetchChantiers();
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    let filtered = chantiers;
    if (statusTab === 'upcoming') filtered = filtered.filter(c => ['Prévu', 'À venir', 'Planifie'].includes(c.statut));
    else if (statusTab === 'in_progress') filtered = filtered.filter(c => ['En cours'].includes(c.statut));
    else if (statusTab === 'done') filtered = filtered.filter(c => ['Terminé', 'Facturé', 'Payé'].includes(c.statut));
    filtered = filtered.filter(
      (c) =>
        c.chantier_id.toLowerCase().includes(q) ||
        (c.ville || '').toLowerCase().includes(q) ||
        (c._project_title || '').toLowerCase().includes(q) ||
        (c.nom_interne || '').toLowerCase().includes(q)
    );
    setFilteredChantiers(filtered);
  }, [search, chantiers, statusTab]);

  const counts = {
    all: chantiers.length,
    upcoming: chantiers.filter(c => ['Prévu', 'À venir', 'Planifie'].includes(c.statut)).length,
    in_progress: chantiers.filter(c => c.statut === 'En cours').length,
    done: chantiers.filter(c => ['Terminé', 'Facturé', 'Payé'].includes(c.statut)).length,
  };

  const fetchChantiers = async () => {
    try {
      const [chRes, devisRes] = await Promise.all([
        api.get('/chantiers'),
        api.get('/devis').catch(() => ({ data: [] })),
      ]);
      const devisProjectMap = {};
      (devisRes.data || []).forEach((d) => {
        devisProjectMap[d.devis_id] = d.project_title || d.titre;
      });
      // On garde TOUS les chantiers, le filtrage se fait via les onglets
      const all = (chRes.data || []).map((c) => ({
        ...c,
        _project_title: c.nom || c.project_title || (c.devis_id ? devisProjectMap[c.devis_id] : null) || '',
      }));
      setChantiers(all);
      setFilteredChantiers(all);
      // Persiste l'ordre pour la navigation précédent/suivant
      try {
        sessionStorage.setItem('chantiers_list_order',
          JSON.stringify(all.map(c => c.chantier_id)));
      } catch (_) {}
    } catch (error) {
      console.error('Erreur chargement chantiers:', error);
      toast.error('Erreur lors du chargement des chantiers');
    } finally {
      setLoading(false);
    }
  };

  const handleChantierCreated = () => {
    setShowForm(false);
    fetchChantiers();
    toast.success('Chantier créé avec succès');
  };

  const getStatutBadgeVariant = (statut) => {
    const variants = {
      'Prévu': 'secondary',
      'En cours': 'default',
      'Terminé': 'success',
      'Bloqué': 'destructive',
    };
    return variants[statut] || 'default';
  };

  const getStatutColor = (statut) => {
    const colors = {
      'Prévu': 'bg-blue-500',
      'En cours': 'bg-green-500',
      'Terminé': 'bg-slate-500',
      'Bloqué': 'bg-red-500',
    };
    return colors[statut] || 'bg-slate-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Chantiers
          </h1>
          <p className="text-sm md:text-base text-slate-600 mt-1">{chantiers.length} chantier(s)</p>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="create-chantier-btn" className="w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Chantier
        </Button>
      </div>

      {/* View mode toggle */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setViewMode('table')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            viewMode === 'table' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
          data-testid="view-table"
        >
          <LayoutGrid className="w-3.5 h-3.5" />
          Tableau
        </button>
        <button
          type="button"
          onClick={() => setViewMode('planning')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            viewMode === 'planning' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
          }`}
          data-testid="view-planning"
        >
          <CalendarDays className="w-3.5 h-3.5" />
          Planning
        </button>
      </div>

      <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm border border-slate-200 space-y-3">
        <div className="flex gap-2 overflow-x-auto">
          {[
            { id: 'all',         label: 'Tous',       count: counts.all },
            { id: 'upcoming',    label: 'À venir',    count: counts.upcoming },
            { id: 'in_progress', label: 'En cours',   count: counts.in_progress },
            { id: 'done',        label: 'Terminés',   count: counts.done },
          ].map(t => (
            <button key={t.id} onClick={() => setStatusTab(t.id)}
              data-testid={`chantiers-tab-${t.id}`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap ${
                statusTab === t.id ? 'bg-[#1C49A6] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}>
              {t.label} <span className="opacity-70">({t.count})</span>
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-chantiers-input"
          />
        </div>
      </div>

      {/* Mobile Cards View */}
      {viewMode === 'table' && (
      <div className="md:hidden space-y-3">
        {filteredChantiers.map((c) => (
          <div
            key={c.chantier_id}
            onClick={() => navigate(`/chantiers/${c.chantier_id}`)}
            className="bg-white rounded-lg p-4 shadow-sm border border-slate-200 active:bg-slate-50 cursor-pointer"
            data-testid={`chantier-card-${c.chantier_id}`}
          >
            <div className="flex items-start justify-between mb-3 gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className={`w-2 h-2 rounded-full shrink-0 ${getStatutColor(c.statut)}`}></div>
                <span className="text-[10px] font-mono text-slate-500 truncate">{c.chantier_id}</span>
              </div>
              <Badge variant={getStatutBadgeVariant(c.statut)} className="text-xs shrink-0">
                {c.statut}
              </Badge>
            </div>
            <div className="space-y-2">
              <p className="font-bold text-slate-900 text-base leading-tight line-clamp-2" title={c._project_title}>
                {c.nom_interne || c._project_title || <span className="italic text-slate-400">Sans titre</span>}
              </p>
              {c.nom_interne && c._project_title && (
                <p className="text-xs text-slate-500 line-clamp-1" title={c._project_title}>{c._project_title}</p>
              )}
              <div className="flex items-center gap-2 text-slate-700 text-sm">
                <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="truncate">{c.ville || 'Ville non définie'}</span>
              </div>
              {c.budget_prevu && (
                <div className="flex items-center gap-2 text-slate-700">
                  <Euro className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="font-semibold text-blue-600 truncate">
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(c.budget_prevu)}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span>{format(new Date(c.date_creation), 'dd/MM/yyyy', { locale: fr })}</span>
              </div>
            </div>
          </div>
        ))}
        {filteredChantiers.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            Aucun chantier trouvé
          </div>
        )}
      </div>
      )}

      {/* Desktop Table View (div-grid pour éviter <span> injectés dans <tbody>) */}
      {viewMode === 'table' && (
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <div className="w-full" role="table">
            <div role="row" className="grid grid-cols-[1fr_2fr_1fr_1fr_1fr_1fr] bg-slate-50 border-b border-slate-200">
              <div role="columnheader" className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">ID Chantier</div>
              <div role="columnheader" className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Projet</div>
              <div role="columnheader" className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Ville</div>
              <div role="columnheader" className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Budget</div>
              <div role="columnheader" className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Statut</div>
              <div role="columnheader" className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Date</div>
            </div>
            <div role="rowgroup" className="divide-y divide-slate-200">
              {filteredChantiers.map((c) => (
                <div
                  key={c.chantier_id}
                  role="row"
                  className="grid grid-cols-[1fr_2fr_1fr_1fr_1fr_1fr] items-center hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/chantiers/${c.chantier_id}`)}
                  data-testid={`chantier-row-${c.chantier_id}`}
                >
                  <div role="cell" className="px-6 py-4 font-mono text-xs text-slate-500">
                    {c.chantier_id}
                  </div>
                  <div role="cell" className="px-6 py-4">
                    <p className="font-semibold text-slate-900 truncate" title={c._project_title}>
                      {c.nom_interne || c._project_title || <span className="italic text-slate-400">Sans titre</span>}
                    </p>
                    {c.nom_interne && c._project_title && (
                      <p className="text-xs text-slate-500 truncate" title={c._project_title}>{c._project_title}</p>
                    )}
                  </div>
                  <div role="cell" className="px-6 py-4 text-sm">{c.ville || '—'}</div>
                  <div role="cell" className="px-6 py-4 font-semibold text-sm">
                    {c.budget_prevu
                      ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(c.budget_prevu)
                      : '-'}
                  </div>
                  <div role="cell" className="px-6 py-4">
                    <Badge variant={getStatutBadgeVariant(c.statut)}>{c.statut}</Badge>
                  </div>
                  <div role="cell" className="px-6 py-4 text-sm text-slate-500">
                    {format(new Date(c.date_creation), 'dd/MM/yyyy', { locale: fr })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Planning / Timeline View */}
      {viewMode === 'planning' && (
        <PlanningView chantiers={filteredChantiers} onOpen={(c) => navigate(`/chantiers/${c.chantier_id}`)} getStatutColor={getStatutColor} />
      )}

      {/* Create Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Nouveau Chantier</DialogTitle>
          </DialogHeader>
          <ChantierForm onSuccess={handleChantierCreated} onCancel={() => setShowForm(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ----------------------- Planning / Timeline view -----------------------
function PlanningView({ chantiers, onOpen, getStatutColor }) {
  const withDates = chantiers.filter((c) => c.date_debut);
  const withoutDates = chantiers.filter((c) => !c.date_debut);

  // Timeline range: min(date_debut) → max(date_fin_estimee | date_debut + 30d), spanning at least 6 months
  const { rangeStart, rangeEnd, months } = useMemo(() => {
    const today = new Date();
    if (withDates.length === 0) {
      const start = startOfMonth(today);
      const end = endOfMonth(addMonths(today, 5));
      const months = [];
      for (let d = new Date(start); d <= end; d = addMonths(d, 1)) months.push(new Date(d));
      return { rangeStart: start, rangeEnd: end, months };
    }
    let minD = null;
    let maxD = null;
    withDates.forEach((c) => {
      try {
        const d1 = parseISO(c.date_debut);
        const d2 = c.date_fin_estimee ? parseISO(c.date_fin_estimee) : addMonths(d1, 1);
        if (!minD || d1 < minD) minD = d1;
        if (!maxD || d2 > maxD) maxD = d2;
      } catch (e) { /* ignore */ }
    });
    if (!minD) minD = today;
    if (!maxD) maxD = addMonths(today, 5);
    const start = startOfMonth(minD < today ? minD : today);
    let end = endOfMonth(maxD);
    // Ensure at least 6 months
    if (differenceInDays(end, start) < 180) end = endOfMonth(addMonths(start, 5));
    const months = [];
    for (let d = new Date(start); d <= end; d = addMonths(d, 1)) months.push(new Date(d));
    return { rangeStart: start, rangeEnd: end, months };
  }, [chantiers]);

  const totalDays = Math.max(differenceInDays(rangeEnd, rangeStart), 1);

  const pctFromDate = (d) => {
    const days = differenceInDays(d, rangeStart);
    return Math.max(0, Math.min(100, (days / totalDays) * 100));
  };

  const barStyle = (c) => {
    try {
      const d1 = parseISO(c.date_debut);
      const d2 = c.date_fin_estimee ? parseISO(c.date_fin_estimee) : addMonths(d1, 1);
      const left = pctFromDate(d1);
      const right = pctFromDate(d2);
      return { left: `${left}%`, width: `${Math.max(right - left, 1.5)}%` };
    } catch (e) {
      return { left: '0%', width: '2%' };
    }
  };

  const todayPct = pctFromDate(new Date());

  return (
    <div className="space-y-6">
      {/* Timeline */}
      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-slate-200" data-testid="planning-timeline">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Timeline chronologique
          </h2>
          <p className="text-xs text-slate-500">
            {format(rangeStart, 'MMM yyyy', { locale: fr })} — {format(rangeEnd, 'MMM yyyy', { locale: fr })}
          </p>
        </div>

        {/* Header months */}
        <div className="relative border border-slate-200 rounded-lg bg-slate-50 overflow-hidden">
          <div className="flex border-b border-slate-200">
            {months.map((m, i) => (
              <div
                key={i}
                className="flex-1 text-center text-[11px] font-semibold text-slate-600 py-1.5 border-r border-slate-200 last:border-r-0 uppercase"
              >
                {format(m, 'MMM yy', { locale: fr })}
              </div>
            ))}
          </div>

          {/* Rows */}
          <div className="relative">
            {/* Today marker */}
            {todayPct > 0 && todayPct < 100 && (
              <div
                className="absolute top-0 bottom-0 w-px bg-red-500 z-10 pointer-events-none"
                style={{ left: `${todayPct}%` }}
                title="Aujourd'hui"
              >
                <div className="absolute -top-1 -translate-x-1/2 text-[9px] bg-red-500 text-white px-1.5 py-0.5 rounded">
                  Aujourd&apos;hui
                </div>
              </div>
            )}

            {withDates.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">
                Aucun chantier avec date de début planifiée.
              </p>
            ) : (
              withDates.map((c) => (
                <div
                  key={c.chantier_id}
                  className="relative h-11 border-b border-slate-100 last:border-b-0 hover:bg-white/70 cursor-pointer group"
                  onClick={() => onOpen(c)}
                  data-testid={`planning-row-${c.chantier_id}`}
                >
                  <div
                    className={`absolute top-1/2 -translate-y-1/2 h-6 rounded-md ${getStatutColor(c.statut)} opacity-90 group-hover:opacity-100 shadow-sm flex items-center px-2 overflow-hidden`}
                    style={barStyle(c)}
                  >
                    <span className="text-[11px] font-medium text-white whitespace-nowrap truncate">
                      {c.nom_interne || c.ville || c.chantier_id}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mt-3 text-xs">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-blue-500"></span> Prévu
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-green-500"></span> En cours
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-slate-500"></span> Terminé
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-red-500"></span> Bloqué
          </span>
        </div>
      </div>

      {/* En attente de planification */}
      <div className="bg-amber-50 rounded-lg p-4 md:p-6 shadow-sm border border-amber-200" data-testid="planning-unplanned">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-amber-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            En attente de planification
          </h2>
          <span className="text-sm font-medium text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">
            {withoutDates.length}
          </span>
        </div>
        {withoutDates.length === 0 ? (
          <p className="text-sm text-amber-800">Tous les chantiers ont une date de début.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {withoutDates.map((c) => (
              <div
                key={c.chantier_id}
                onClick={() => onOpen(c)}
                className="bg-white rounded-md p-3 border border-amber-200 hover:border-amber-400 cursor-pointer transition-colors"
                data-testid={`unplanned-card-${c.chantier_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {c.nom_interne || c.ville || 'Chantier'}
                    </p>
                    <p className="text-xs text-slate-500 truncate font-mono">{c.chantier_id}</p>
                  </div>
                  <Badge variant="secondary" className="text-[10px]">{c.statut}</Badge>
                </div>
                {c.budget_prevu && (
                  <p className="text-xs text-slate-600 mt-2">
                    Budget : <span className="font-semibold">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(c.budget_prevu)}</span>
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}